import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import SettingsWidget from './SettingsWidget';
import {
  FilterItem,
  FilterSetting,
  SearchSettings,
  InputValues,
} from './InterfaceTypes';
import { useTranslate } from '@im/react-localization';
import { isEmbedded } from 'src/util';

interface AdvancedSearchSettingProps {
  businessType?: string | null;
  filterSettings: FilterSetting[];
  filtersItems: FilterItem[];
  handleClose?: () => void;
  onClearAll?: () => void;
  onSearch: (searchData: SearchSettings) => void;
  open: boolean;
  searchSettings?: SearchSettings;
}

export default function AdvancedSearchSetting(
  props: Readonly<AdvancedSearchSettingProps>
) {
  const translate = useTranslate();
  const [searchData, setSearchData] = useState<SearchSettings>(
    props.searchSettings ?? {}
  );
  const [vendorId, setVendorId] = useState<number>(0);

  const onVendorIdChange = (vendorId: number) => {
    if (vendorId === 0 && searchData['program']) {
      searchData['program'][0].value = '';
    }
    setVendorId(vendorId);
  };

  const onClose = () => {
    if (props.businessType === 'Subscription') {
      setVendorId(0);
    }
    props.handleClose?.();
  };
  const onChange = (value: string, data: InputValues[]) => {
    const _searchData = {
      ...searchData,
      [value]: data,
    };
    if (data.every((d) => d.value === '')) {
      delete _searchData[value];
    }
    setSearchData(_searchData);
  };
  useEffect(() => {
    if (props.open) {
      setSearchData(props.searchSettings ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (
      props?.searchSettings &&
      Object.keys(props?.searchSettings).length === 0
    ) {
      setSearchData({});
    }
  }, [props.searchSettings]);
  const isDirty = useMemo(() => {
    let _isDirty = true;
    if (Object.keys(searchData).length === 0) return true;
    for (const key in searchData) {
      _isDirty = searchData[key].some((v) =>
        typeof v.value === 'string'
          ? v.value.trim() === ''
          : Object.values(v.value).some((va) => va === '')
      );
      if (_isDirty) return _isDirty;
    }
    return _isDirty;
  }, [searchData]);
  return (
    <Drawer
      anchor={'right'}
      data-testid="gds-vp-advanced-search-filter-drawer"
      onClose={onClose}
      open={props.open}
      sx={{
        '.MuiDrawer-paperAnchorRight': {
          height: isEmbedded() ? '100vh' : `calc(100vh - 50px)`,
          top: isEmbedded() ? 0 : 50,
          width: 680,
        },
      }}
    >
      <Box
        data-testid="gds-vp-advanced-search-filter-drawer-header"
        paddingBottom={1}
        paddingTop={2}
        paddingX={2}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography
            sx={(theme) => ({
              alignContent: 'center',
              display: 'grid',
              fontSize: theme.typography.body1.fontSize,
            })}
            variant="smalltextsemibold"
          >
            {translate('VendorProgram.AdvancedSearch')}
          </Typography>
          <Stack alignSelf="flex-end" direction="row" gap={1}>
            {props.onClearAll && (
              <Button
                onClick={props.onClearAll}
                sx={(theme) => ({
                  color: `${theme.palette.text.primary} !important`,
                })}
                variant="newText"
              >
                {translate('VendorDeals.ClearAll')}
              </Button>
            )}
            <IconButton
              data-testid="gds-vp-ass-closeicon"
              onClick={onClose}
              size="small"
              sx={{ alignSelf: 'baseline' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
      <Box overflow="auto" p={2}>
        {props.filtersItems.map((item) => (
          <SettingsWidget
            businessType={props.businessType ?? null}
            filterItem={item}
            filterSettings={props.filterSettings}
            key={item.value}
            onVendorIdChange={onVendorIdChange}
            searchData={onChange}
            settingsData={Object.values(searchData?.[item.value] ?? [])}
            vendorId={vendorId}
          />
        ))}
      </Box>
      <Stack
        direction="row"
        justifyContent={'flex-end'}
        marginTop="auto"
        paddingX={2}
        paddingY={3}
        spacing={1.5}
      >
        <Button
          data-testid="gds-vp-advanced-search-filter-drawer-cancel-btn"
          onClick={onClose}
          variant="newOutlined"
        >
          {translate('Common.Cancel')}
        </Button>
        <Button
          data-testid="gds-vp-advanced-search-filter-drawer-search-deals-btn"
          disabled={isDirty}
          onClick={() => props.onSearch(searchData)}
          variant="newContained"
        >
          {translate('VendorDeals.SearchDeals')}
        </Button>
      </Stack>
    </Drawer>
  );
}
